// SETTINGS V2 — UI state slice (SETTINGS_V2_SP0 §7.1).
//
// Own slice so no legacy settings slice is mutated by v2 work. Holds ONLY UI
// state — data slices (customerAccount etc.) are reused unchanged.
//
// SEC-REQ-2: the module-scope readTheme() of the NON-auth "settings-v2-theme"
// key is permitted (same pattern as midasV3Slice; a UI preference, not an auth
// key). No network calls, no auth-state reads/writes at import time.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ============================================================
// Types
// ============================================================

export type SettingsV2Theme = "claude" | "machineid";
/** SP2 widened this to carry the change-password dialog id; SP3 adds the
 *  contact create/edit dialog and the delete-contact confirm dialog; SP4 adds
 *  the location create/edit dialog and the disable-location confirm dialog; SP6
 *  adds the two invite dialogs and the delete-user confirm dialog; SP9 adds the
 *  shipping edit dialog; LOC-DELETE adds the permanent-delete-location confirm.
 *  null = no dialog open. */
export type SettingsV2DialogId =
  | "changePassword"
  | "contact"
  | "deleteContact"
  | "location"
  | "disableLocation"
  | "deleteLocation"
  | "invitePermanent"
  | "issueGuestPass"
  /** DEMO_USER_TYPE_SPEC F1 — same dialog component as "issueGuestPass", opened
   *  with the pass-type pre-set to Demo (Admin-only entry point). */
  | "issueDemoPass"
  | "deleteUser"
  | "shipping"
  | null;

interface SettingsV2UiState {
  /** Active palette class — `.p-claude` (default) or `.p-machineid`. */
  theme: SettingsV2Theme;
  /** Nav key of the current section; default "profile". */
  activeSection: string;
  /** Open dialog id — null in SP0; SP2+ dialogs register ids. */
  openDialog: SettingsV2DialogId;
  /** Phone bottom-sheet visibility — SP1 consumes; state seam ships now. */
  phoneNavOpen: boolean;
}

// ============================================================
// localStorage hydration (mirrors midasV3Slice.ts lines 89-117)
// ============================================================

const LS_THEME_KEY = "settings-v2-theme";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readTheme = (): SettingsV2Theme => {
  const ls = safeLocalStorage();
  if (!ls) return "claude";
  const raw = ls.getItem(LS_THEME_KEY);
  if (raw === "claude" || raw === "machineid") return raw;
  return "claude";
};

const writeTheme = (theme: SettingsV2Theme): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_THEME_KEY, theme);
  } catch {
    // localStorage may throw in private mode / quota; not fatal for v2 UX.
  }
};

// ============================================================
// Slice
// ============================================================

const initialState: SettingsV2UiState = {
  theme: readTheme(),
  activeSection: "profile",
  openDialog: null,
  phoneNavOpen: false,
};

const settingsV2UiSlice = createSlice({
  name: "settingsV2Ui",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<SettingsV2Theme>) => {
      state.theme = action.payload;
      writeTheme(action.payload);
    },
    setActiveSection: (state, action: PayloadAction<string>) => {
      state.activeSection = action.payload;
    },
    // Stub in SP0: SettingsV2DialogId is `null` today, so this is effectively a
    // no-op until the union widens in SP2+ (then it carries real dialog ids).
    // NOTE (spec deviation): the spec's `PayloadAction<NonNullable<...>>` types
    // the payload as `never`, which RTK's createSlice rejects (a never-payload
    // case reducer isn't assignable to CaseReducer<State, {payload: any}>). Typed
    // as `SettingsV2DialogId` instead — compiles and widens naturally.
    openDialog: (state, action: PayloadAction<SettingsV2DialogId>) => {
      state.openDialog = action.payload;
    },
    closeDialog: (state) => {
      state.openDialog = null;
    },
    setPhoneNavOpen: (state, action: PayloadAction<boolean>) => {
      state.phoneNavOpen = action.payload;
    },
  },
});

export const {
  setTheme,
  setActiveSection,
  openDialog,
  closeDialog,
  setPhoneNavOpen,
} = settingsV2UiSlice.actions;

export default settingsV2UiSlice.reducer;
